/**
 * In-memory store behind the mock provider.
 *
 * Holds a few sample records and a tiny pub/sub so subscriptions behave like
 * the real thing. Nothing persists: a refresh resets everything.
 *
 * This exists so the app runs with no Firebase project and no server, which
 * is how we work while the screens are being built.
 */

import { newId, directChatId } from '../../utils/id.js'

const HOUR = 60 * 60 * 1000
const DAY  = 24 * HOUR
const now  = Date.now()

/** The signed-in user in mock mode. */
export const ME = 'u_me'

export const state = {
  currentUserId: ME,

  // The session, kept apart from the identity above. The seeded chats and
  // messages all reference ME, so currentUserId must stay set for them to
  // resolve — but the app should still open on the welcome screen rather
  // than dropping straight into someone's conversations. Sign in with any
  // phone number and any six digits.
  isSignedIn: false,

  users: new Map([
    // No displayName on purpose. Signing in on mock should behave like a
    // brand-new account, which means landing on the profile screen — the
    // same thing the real server does, where a new row's display_name is ''.
    // Seeding a name here made the app skip that step entirely.
    [ME,     { id: ME,     phone: '+2348000000001', displayName: '',          about: 'Available',        avatarUrl: null, isOnline: true,  lastSeen: new Date(now) }],
    ['u_002', { id: 'u_002', phone: '+2348000000002', displayName: 'Contact Two',   about: 'Busy',        avatarUrl: null, isOnline: true,  lastSeen: new Date(now) }],
    ['u_003', { id: 'u_003', phone: '+2348000000003', displayName: 'Contact Three', about: 'At work',     avatarUrl: null, isOnline: false, lastSeen: new Date(now - 2 * HOUR) }],
    ['u_004', { id: 'u_004', phone: '+2348000000004', displayName: 'Contact Four',  about: 'Hey there',   avatarUrl: null, isOnline: false, lastSeen: new Date(now - DAY) }]
  ]),

  chats: new Map(),
  messages: new Map(),   // chatId -> Message[]
  statuses: new Map(),

  /*
   * Channels. Seeded empty, deliberately.
   *
   * Every other map above carries obviously-fake placeholders — "Contact Two",
   * "Sample Group" — which read as scaffolding. A channel cannot: a plausible
   * name with a follower count beside it reads as a real publisher who never
   * agreed to appear here. So the mock starts with none and the create screen
   * fills it.
   *
   * Each record keeps its own followers, admins and posts rather than three
   * parallel maps; the real backend normalises them into tables, and this one
   * only has to behave the same from the outside.
   */
  channels: new Map(),   // channelId -> { ...channel, followers: Map, admins: Map, posts: [] }

  /*
   * Communities. Seeded empty, like channels and for the same reason.
   *
   * Their GROUPS are not here — those are ordinary rows in state.chats
   * carrying a communityId, exactly as they are ordinary rows in the chats
   * table carrying a community_id. Keeping them anywhere else would hide
   * the day that stops being true.
   */
  communities: new Map(),  // communityId -> { ...community, members: Map }
  calls: new Map(),
  contacts: new Map(),
  blocked: new Set(),
  typing: new Map()      // chatId -> Set<userId>
}

// ---------------------------------------------------------------------------
// Seed
// ---------------------------------------------------------------------------

function seedChat({ id, type, name, participantIds, createdBy }) {
  state.chats.set(id, {
    id, type, name,
    description: null,
    avatarUrl: null,
    participantIds,
    participants: participantIds.map(userId => ({
      userId,
      role: userId === createdBy ? 'admin' : 'member',
      joinedAt: new Date(now - 30 * DAY)
    })),
    lastMessage: null,
    lastMessageAt: null,
    userState: {},
    createdBy,
    createdAt: new Date(now - 30 * DAY)
  })
  state.messages.set(id, [])
}

function seedMessage(chatId, { senderId, body, minutesAgo, type = 'text', replyToId = null }) {
  const message = {
    id: newId(),
    chatId,
    senderId,
    type,
    body,
    mediaUrl: null,
    mediaMeta: null,
    replyToId,
    status: senderId === ME ? 'read' : 'sent',
    reactions: {},
    createdAt: new Date(now - minutesAgo * 60_000),
    editedAt: null,
    deletedAt: null
  }
  state.messages.get(chatId).push(message)

  const chat = state.chats.get(chatId)
  chat.lastMessage = {
    id: message.id,
    senderId,
    type,
    preview: body,
    createdAt: message.createdAt
  }
  chat.lastMessageAt = message.createdAt
  return message
}

const directA = directChatId(ME, 'u_002')
const directB = directChatId(ME, 'u_003')
const groupA  = 'c_group_001'

seedChat({ id: directA, type: 'direct', name: null, participantIds: [ME, 'u_002'], createdBy: ME })
seedChat({ id: directB, type: 'direct', name: null, participantIds: [ME, 'u_003'], createdBy: 'u_003' })
seedChat({ id: groupA,  type: 'group',  name: 'Sample Group', participantIds: [ME, 'u_002', 'u_003', 'u_004'], createdBy: ME })

const firstIn = seedMessage(directA, { senderId: 'u_002', body: 'Sample incoming message', minutesAgo: 50 })
const myReply = seedMessage(directA, { senderId: ME, body: 'Sample outgoing reply', minutesAgo: 45,
  replyToId: firstIn.id })
seedMessage(directA, { senderId: 'u_002', body: 'Another incoming one', minutesAgo: 8,
  replyToId: myReply.id })

seedMessage(directB, { senderId: 'u_003', body: 'Message in the second chat', minutesAgo: 240 })

seedMessage(groupA, { senderId: 'u_004', body: 'Group message one', minutesAgo: 180 })
seedMessage(groupA, { senderId: ME,      body: 'Group message two', minutesAgo: 120 })

state.chats.get(directA).userState[ME] = { unreadCount: 1, muted: false, pinned: false, archived: false }

for (const [id, user] of state.users) {
  if (id === ME) continue
  state.contacts.set(id, { id: `ct_${id}`, userId: id, savedName: user.displayName, user })
}

state.statuses.set('s_001', {
  id: 's_001',
  userId: 'u_002',
  type: 'text',
  body: 'Sample status update',
  mediaUrl: null,
  backgroundColor: null,
  font: null,
  viewCount: 0,
  viewedBy: [],
  createdAt: new Date(now - 3 * HOUR),
  expiresAt: new Date(now - 3 * HOUR + DAY)
})

// ---------------------------------------------------------------------------
// Pub/sub
// ---------------------------------------------------------------------------

const listeners = new Map()   // topic -> Set<callback>

/**
 * Subscribe to a topic. Returns an unsubscribe function, matching the shape
 * every contract requires.
 */
export function subscribe(topic, callback) {
  if (!listeners.has(topic)) listeners.set(topic, new Set())
  listeners.get(topic).add(callback)
  return () => listeners.get(topic)?.delete(callback)
}

export function emit(topic, payload) {
  for (const callback of listeners.get(topic) ?? []) {
    // Async so callers see the same ordering they would with a real network
    queueMicrotask(() => callback(payload))
  }
}

/** Pretend the network took a moment, so loading states are exercised. */
export function latency(ms = 120) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
